import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseFormSetValue } from "react-hook-form";
import {
  getIncidentRecordById,
  confirmIncidentRecordFile,
  acknowledgeIncidentRecordFile,
  deleteIncidentRecordFile,
  startOngoingIncidentRecords,
} from "@phrmpc/infrastructure/phrmpc/api/incident-records";
import { getAllIncidentFileFieldTypes } from "@phrmpc/infrastructure/phrmpc/api/incident-file-field-types";
import { customInstance } from "@phrmpc/infrastructure/phrmpc/axios";
import type { IncidentRecordFileInfoDto } from "@phrmpc/infrastructure/phrmpc/models";
import type { IncidentFormValues } from "../types";
import { buildFileFieldTypeMap } from "../utils/fileFieldType";

/**
 * Shared store for an IncidentRecord's files - fetch, upload, delete, confirm, acknowledge - so
 * every section that touches files (MonitoringCaseSection via useCaseDocumentChain,
 * IncidentUploadCell) shares one real record/files fetch and one implementation of each
 * operation, instead of each section building its own queries/mutations. Consolidates what was
 * previously duplicated between useCaseDocumentChain.ts's mutations and IncidentUploadCell's
 * inline uploadFn/removeFn (see Bug 157798's root cause note, fixed independently in both places
 * before this consolidation existed).
 */
export interface IncidentRecordFileStore {
  files: IncidentRecordFileInfoDto[];
  /** Resolves the single file matching a field type + signed flag. `matchIndex` (default 0)
   *  picks the Nth match by upload order (createDate) rather than just the first - needed for
   *  AWOL's NOH 1/NOH 2, which share one fileFieldTypeId and are distinguished only by upload
   *  order. Supersedes the old findFile/findMatch helpers duplicated in IncidentForm.tsx and
   *  useCaseDocumentChain.ts. */
  getFile: (
    fileFieldTypeId: number,
    isSignedDocument?: boolean,
    matchIndex?: number,
  ) => IncidentRecordFileInfoDto | undefined;
  /** Resolves every file matching a field type + signed flag, sorted by upload order (createDate) -
   *  for `multiple` fields (e.g. Evidence), where `getFile`'s single-match/matchIndex shape doesn't
   *  fit. */
  getFiles: (
    fileFieldTypeId: number,
    isSignedDocument?: boolean,
  ) => IncidentRecordFileInfoDto[];
  upload: (
    file: File,
    fileFieldTypeId: number,
    isSignedDocument: boolean,
    /** Reports 0-100 upload percentage, when the caller wants to display it. */
    onProgress?: (percent: number) => void,
    /** The unsigned file this signed upload belongs to - required for AWOL's NOH 1/NOH 2, which
     *  share one fileFieldTypeId, so `getFile` can match the signed copy back to the right slot
     *  via `sourceUnsignedId` instead of guessing positionally (Bug 158179). */
    sourceUnsignedId?: string,
  ) => Promise<{ name: string }>;
  remove: (fileId: string) => Promise<void>;
  confirm: (fileId: string) => Promise<void>;
  acknowledge: (fileId: string, isAcknowledged: boolean) => Promise<void>;
}

const IncidentRecordFileContext = createContext<
  IncidentRecordFileStore | undefined
>(undefined);

export const IncidentRecordFileProvider = ({
  incidentRecordId,
  setValue,
  children,
}: {
  incidentRecordId: string | undefined;
  /**
   * Bug 157821 follow-up: mirrors the fetched `files` collection into the page's RHF state as
   * `incidentRecordFiles`, so the submit endpoint's `Payload.IncidentRecordFiles`-keyed 400
   * (SubmitIncidentRecordValidator.cs: "An Incident Report Attachment file must be uploaded
   * before submitting.") has a real field to setError against - see IncidentUploadCell's
   * `extraErrorNames` for where that error actually renders. The backend validates against its
   * own persisted entity state, not this payload field, so nothing here writes back to the
   * server - this is purely to give the client-side error-mapping pipeline a home for that error.
   */
  setValue?: UseFormSetValue<IncidentFormValues>;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();

  // Deliberately its own query key, distinct from IncidentForm.tsx's `["incident-record", id]` -
  // that key is bound as the page's RHF `values` (a "controlled form" per react-hook-form's
  // `values` option), which fully resets the form whenever its reference changes. Sharing the key
  // meant every file upload/delete/confirm/acknowledge - none of which touch any actual form
  // field, since file data is deliberately excluded from IncidentFormValues - invalidated and
  // refetched the page's query too, resetting the entire form (including unrelated in-progress
  // edits) on every checkbox tick or upload. Costs one extra initial fetch of the same endpoint;
  // worth it for correctness.
  const { data: recordData } = useQuery({
    queryKey: ["incident-record-files", incidentRecordId],
    queryFn: () => getIncidentRecordById(incidentRecordId!),
    enabled: Boolean(incidentRecordId),
  });
  const files = recordData?.incidentRecordFiles ?? [];

  useEffect(() => {
    setValue?.("incidentRecordFiles", files, { shouldDirty: false });
  }, [files, setValue]);

  const { data: fileFieldTypes = [] } = useQuery({
    queryKey: ["incident-file-field-types"],
    queryFn: () => getAllIncidentFileFieldTypes(),
  });
  const fileFieldType = buildFileFieldTypeMap(fileFieldTypes);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["incident-record-files", incidentRecordId],
    });

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      fileFieldTypeId,
      isSignedDocument,
      onProgress,
      sourceUnsignedId,
    }: {
      file: File;
      fileFieldTypeId: number;
      isSignedDocument: boolean;
      onProgress?: (percent: number) => void;
      sourceUnsignedId?: string;
    }) => {
      // Bypasses the generated uploadFilesIncidentRecords wrapper: orval's codegen builds its
      // *own* FormData internally from plain object-property access on its second argument
      // (`body.files`/`body.fileFieldTypeId`/...), so passing a pre-built FormData there (as
      // this codebase's other upload call sites do) silently sends an empty body - none of
      // `body`'s properties exist on a FormData instance, so every `if (... !== undefined)`
      // check fails and nothing gets appended. Calling customInstance directly with a real
      // FormData is the only way to actually upload file bytes through this endpoint
      // (Bug 157798).
      const formData = new FormData();
      formData.append("files", file);
      formData.append("fileFieldTypeId", String(fileFieldTypeId));
      formData.append("isSignedDocument", String(isSignedDocument));
      if (sourceUnsignedId) {
        formData.append("sourceUnsignedId", sourceUnsignedId);
      }
      await customInstance<void>({
        url: `/incident-management/incident-records/${incidentRecordId}/files`,
        method: "POST",
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
        // The shared axios client's default timeout (axios.ts) was too short for file transfers
        // even after raising it to 3 minutes there - confirmed live (Bug 157949 Result 1): a
        // 25MB upload aborted with a generic "Network Error" at ~10013ms under the old 10s
        // default. Upload progress is already surfaced via onUploadProgress, so there's no UX
        // reason to bound this request at all - disable the timeout for uploads specifically.
        timeout: 0,
        onUploadProgress: onProgress
          ? (event) =>
              onProgress(
                event.total ? Math.round((event.loaded / event.total) * 100) : 0,
              )
          : undefined,
      });
      return { name: file.name };
    },
    onSuccess: async (_result, { fileFieldTypeId }) => {
      // Bug 157820: uploading the case-opening document (NTE Letter for Minor/Major, AWOL
      // Letter for AWOL) is what should move a Created record to On-going, per the backend's
      // /ongoing endpoint eligibility rule - nothing previously called it, so the status stayed
      // on Created after upload. Only fire it from the record's Created state so re-uploads or
      // uploads to an already On-going record don't attempt a redundant/invalid transition.
      if (
        incidentRecordId &&
        recordData?.statusName === "Created" &&
        (fileFieldTypeId === fileFieldType.NOTICE_OF_EXPLANATION ||
          fileFieldTypeId === fileFieldType.AWOL_LETTER)
      ) {
        await startOngoingIncidentRecords(incidentRecordId, {
          id: incidentRecordId,
        });
        await queryClient.invalidateQueries({
          queryKey: ["incident-record", incidentRecordId],
        });
      }
      await invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) =>
      deleteIncidentRecordFile(incidentRecordId!, fileId),
    onSuccess: invalidate,
  });

  const confirmMutation = useMutation({
    mutationFn: (fileId: string) =>
      confirmIncidentRecordFile(incidentRecordId!, fileId, {}),
    onSuccess: invalidate,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: ({
      fileId,
      isAcknowledged,
    }: {
      fileId: string;
      isAcknowledged: boolean;
    }) =>
      acknowledgeIncidentRecordFile(incidentRecordId!, fileId, {
        isAcknowledged,
      }),
    onSuccess: invalidate,
  });

  const store = useMemo<IncidentRecordFileStore>(
    () => ({
      files,
      getFile: (fileFieldTypeId, isSignedDocument = false, matchIndex = 0) => {
        const unsignedMatches = files
          .filter(
            (f) =>
              f.fileFieldTypeId === fileFieldTypeId &&
              !f.isSignedDocument,
          )
          .sort(
            (a, b) =>
              (a.createDate?.getTime() ?? 0) - (b.createDate?.getTime() ?? 0),
          );

        if (!isSignedDocument) {
          return unsignedMatches[matchIndex];
        }

        // A signed file is matched to its NOH slot via `sourceUnsignedId`, not positional
        // order within the signed-only subset - AWOL's NOH 1/NOH 2 share one fileFieldTypeId,
        // so if only NOH 2 has a signed copy uploaded (NOH 1 not yet acknowledged/signed), a
        // plain index into the signed-only list would wrongly surface NOH 2's file under NOH
        // 1's slot (matchIndex 0). Falls back to the old positional match only when
        // `fileFieldTypeId` isn't paired (at most one unsigned file exists for it) - for paired
        // (AWOL NOH) fields, a slot with no unsigned counterpart of its own cannot have a signed
        // file at all, so returning undefined there (Bug 158179) is correct instead of guessing.
        const targetUnsignedId = unsignedMatches[matchIndex]?.id;
        const signedMatches = files
          .filter(
            (f) =>
              f.fileFieldTypeId === fileFieldTypeId &&
              Boolean(f.isSignedDocument),
          )
          .sort(
            (a, b) =>
              (a.createDate?.getTime() ?? 0) - (b.createDate?.getTime() ?? 0),
          );

        if (targetUnsignedId) {
          const bySourceId = signedMatches.find(
            (f) => f.sourceUnsignedId === targetUnsignedId,
          );
          if (bySourceId) return bySourceId;
        }

        if (unsignedMatches.length > 1) {
          return undefined;
        }

        return signedMatches[matchIndex];
      },
      getFiles: (fileFieldTypeId, isSignedDocument = false) =>
        files
          .filter(
            (f) =>
              f.fileFieldTypeId === fileFieldTypeId &&
              Boolean(f.isSignedDocument) === isSignedDocument,
          )
          .sort(
            (a, b) =>
              (a.createDate?.getTime() ?? 0) - (b.createDate?.getTime() ?? 0),
          ),
      upload: (
        file,
        fileFieldTypeId,
        isSignedDocument,
        onProgress,
        sourceUnsignedId,
      ) =>
        uploadMutation.mutateAsync({
          file,
          fileFieldTypeId,
          isSignedDocument,
          onProgress,
          sourceUnsignedId,
        }),
      remove: (fileId) => deleteMutation.mutateAsync(fileId).then(() => undefined),
      confirm: (fileId) => confirmMutation.mutateAsync(fileId).then(() => undefined),
      acknowledge: (fileId, isAcknowledged) =>
        acknowledgeMutation
          .mutateAsync({ fileId, isAcknowledged })
          .then(() => undefined),
    }),
    [files, uploadMutation, deleteMutation, confirmMutation, acknowledgeMutation],
  );

  return (
    <IncidentRecordFileContext.Provider value={store}>
      {children}
    </IncidentRecordFileContext.Provider>
  );
};

export const useIncidentRecordFiles = (): IncidentRecordFileStore => {
  const store = useContext(IncidentRecordFileContext);
  if (!store) {
    throw new Error(
      "useIncidentRecordFiles must be used within an IncidentRecordFileProvider",
    );
  }
  return store;
};
